/**
 * The legacy → 2.1 migration for reference nodes (SCENE_ROOT_SPEC §2.3,
 * "Existing content — migrated at the save that converts it").
 *
 * Scenes saved before the placement rule carry the referenced document's ROOT
 * behaviors on the reference node itself: the old import made the document's
 * root BE the placement, and the save wrote its behaviors onto the scene's
 * node. The next open builds the correct tree — `R > D.root > …` — and every
 * property the root declares is then declared twice. With `inherits: true` the
 * inner (real) declaration loses to the ancestor copy (Canopy step 4), and an
 * AutoUI slider commits into the one that no longer drives anything: the ESPN
 * Remote stage slider, dead in Studio and in the app.
 *
 * No reader corrects that — a reader that rewrote authored data would be
 * guessing on every load, forever. The correction happens ONCE, where the data
 * crosses the format boundary: when a scene whose source format was legacy is
 * written as a 2.1 folder document, a behavior on a reference node that is
 * deep-equal to one on the referenced document's root IS the import-era copy,
 * and it is not written.
 *
 * **Safe by construction.** A deep-equal copy contributes exactly what the
 * root already declares, so dropping it changes no loaded value; it only stops
 * the copy shadowing the root's own declaration at runtime. Anything the user
 * actually edited differs from the root and is written as-is — that is the
 * override mechanism (§2.3 "Editing"), not a copy.
 *
 * Never applied on read, and never for a scene that was already 2.1: there the
 * import built the right tree in the first place.
 *
 * Deleted with the legacy readers (#816) — after which no scene can reach a
 * 2.1 save from a legacy open.
 */

import { Logger } from '../utils/Logger';
import type { BehaviorData } from '../scenedata/behaviordata';
import type { SceneObjectData } from '../scenedata/sceneobjectdata';
import type { SceneObject } from '../core/SceneObject';

/**
 * A behavior's identity for the comparison: its `@type` plus its whole payload
 * with keys in a stable order, so two records that differ only in the order
 * their fields were written compare equal.
 */
function behaviorSignature(behavior: BehaviorData): string {
    const stable = (value: unknown): unknown => {
        if (Array.isArray(value)) return value.map(stable);
        if (value && typeof value === 'object') {
            const source = value as Record<string, unknown>;
            const ordered: Record<string, unknown> = {};
            for (const key of Object.keys(source).sort()) ordered[key] = stable(source[key]);
            return ordered;
        }
        return value;
    };
    return JSON.stringify(stable(behavior));
}

/**
 * Drop import-era copies from `content`, in place.
 *
 * A record is a candidate when its live object is a reference whose content
 * came from a document — which, in the live scene, means exactly one child:
 * the document's root node. (A multi-root document has no single root to have
 * been copied from, and the bug this repairs could only ever produce one
 * copy.) Its serialized behaviors are compared against the ROOT's, through the
 * same serialization, so the two sides are always in the same form.
 *
 * @param content - the prepared export records, mutated in place
 * @param liveFor - the live SceneObject a record was captured from, if known
 * @returns how many behaviors were dropped
 */
export function dropImportEraReferenceCopies(
    content: SceneObjectData[],
    liveFor: (record: SceneObjectData) => SceneObject | undefined,
): number {
    let dropped = 0;

    for (const record of content) {
        if (!record.behaviors?.length) continue;
        const live = liveFor(record);
        if (!live?.isSceneReference) continue;

        const children = live.getChildren();
        if (children.length !== 1) continue;
        const documentRoot = children[0];

        const rootSignatures = new Set(documentRoot.serializeBehaviors().map(behaviorSignature));
        if (rootSignatures.size === 0) continue;

        record.behaviors = record.behaviors.filter(behavior => {
            if (!rootSignatures.has(behaviorSignature(behavior))) return true;
            dropped++;
            Logger.log(
                `[SceneExporter] legacy → 2.1: dropped "${behavior['@type']}" from reference `
                + `"${record.name}" — an import-era copy of the same behavior on the referenced `
                + `document's root ("${documentRoot.name}"). SCENE_ROOT_SPEC §2.3.`,
            );
            return false;
        });
    }

    return dropped;
}
